import CustomReport from '@models/customReport';
import { getInitialSessionBudget } from '../SalesAttribution/SalesAttributionView/Summary/saveLoadAdSpend';
import { campaignFromPayload } from './responseMappers/campaign';
import { ReportType } from '../hooks/useCurrentReportType';
import { CampaignPayload } from './schemas/campaign';
import { Temporal } from 'temporal-polyfill';

const MIN_BUDGET_DISPLAY_THRESHOLD = 1000;

const campaignRemainingDays = (campaignEndDate: string) => {
  const endDate = Temporal.PlainDate.from(campaignEndDate);
  const current = Temporal.Now.plainDateISO();
  const days = endDate.since(current).days;
  return days + 1;
};

// This is to help transition from all the logic in the campaign model file into a solution where everything is static and pre-computed.
// Instead of just reusing the old campaign model, pass only individual fields as needed to prevent over dependence on legacy code
export type LegacyCampaignFields = {
  canDisplayVisitCosts: boolean;
  isValidBudget: boolean;
  doesMeasureLCI: boolean;
  isPartner: boolean;
  remainingDays: number;
  approvedReportDates: string[];
  large: boolean;
  reportGroup: string | null;
  reportId: number | null;
  partner: string | null;
  customReports: CustomReport[];
  salesAttributionRequested: boolean;
};

export const getLegacyCampaignFields = (
  legacyCampaign: CampaignPayload,
  updatedCampaign: ReturnType<typeof campaignFromPayload>,
  reportType: ReportType | undefined = 'master_report'
): LegacyCampaignFields => {
  const sessionBudgetStore = getInitialSessionBudget();
  const sessionBudget = sessionBudgetStore?.[updatedCampaign.id];
  const budgetOrAdSpend =
    getDisplayedAdSpend(
      sessionBudget,
      updatedCampaign.adSpend,
      updatedCampaign.budget
    ) ?? 0;

  const isValidBudget = budgetOrAdSpend >= MIN_BUDGET_DISPLAY_THRESHOLD;

  const approvedReportDates = legacyCampaign.approved_report_dates.map(
    reportDateObject => reportDateObject.report_date
  );

  return {
    canDisplayVisitCosts: reportType === 'master_report' && isValidBudget,
    isValidBudget,
    doesMeasureLCI: legacyCampaign.measurement_status === 'approved',
    isPartner: !!legacyCampaign.partner,
    remainingDays: campaignRemainingDays(legacyCampaign.end_date),
    approvedReportDates,
    large: legacyCampaign.large,
    reportGroup: legacyCampaign.report_group,
    reportId: legacyCampaign.report_id,
    partner: legacyCampaign.partner,
    customReports: legacyCampaign.custom_reports,
    salesAttributionRequested: legacyCampaign.sales_attribution_requested,
  };
};

export const getCalculatedFields = (
  campaign: ReturnType<typeof campaignFromPayload>,
  sessionBudget: number | null | undefined
) => {
  return {
    sessionBudget: getDisplayedAdSpend(
      sessionBudget,
      campaign.adSpend,
      campaign.budget
    ),
  };
};

const getDisplayedAdSpend = (
  sessionBudget: number | null | undefined,
  adSpend: number | null | undefined,
  budget: number | null | undefined
) => {
  if (sessionBudget && sessionBudget > 0) {
    return sessionBudget;
  }

  if (adSpend && adSpend > 0) {
    return adSpend;
  }

  return budget ?? 0;
};
